import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useUser } from '@/contexts/UserContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Users, Key, LogOut, Activity, TrendingUp, Calendar, ChevronRight, User, Mail } from 'lucide-react';
import BrandMark from '@/components/BrandMark';
import { getBaseUrl } from '@/utils/api';

const API_URL = getBaseUrl();

const CoachDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useUser();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedClient, setSelectedClient] = useState(null);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchClients();
  }, [user, navigate]);

  const fetchClients = async () => {
    try {
      const response = await fetch(`${API_URL}/api/coaches/${user.id}/clients`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setClients(data);
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (e) {}
    logout();
    navigate('/login');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-ivory-mist flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-burnt-terra"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ivory-mist">
      {/* Header */}
      <header className="bg-white border-b border-shadow-pine/10 px-6 py-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div>
            <h1 className="font-display text-2xl font-bold text-obsidian-night">Coach Dashboard</h1>
            <p className="text-shadow-pine/60 font-body text-sm">Welcome, {user?.name}</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="bg-burnt-terra/10 px-4 py-2 rounded-lg">
              <span className="text-sm text-shadow-pine/60 font-body">Your ID Key:</span>
              <span className="ml-2 font-mono font-bold text-burnt-terra">{user?.coach_id_key}</span>
            </div>
            <Button variant="outline" onClick={handleLogout} className="border-shadow-pine/30">
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="bg-white">
            <CardContent className="pt-6">
              <div className="flex items-center">
                <div className="w-12 h-12 bg-burnt-terra/10 rounded-full flex items-center justify-center">
                  <Users className="w-6 h-6 text-burnt-terra" />
                </div>
                <div className="ml-4">
                  <p className="text-sm text-shadow-pine/60 font-body">Total Clients</p>
                  <p className="text-2xl font-display font-bold text-obsidian-night">{clients.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white">
            <CardContent className="pt-6">
              <div className="flex items-center">
                <div className="w-12 h-12 bg-shadow-pine/10 rounded-full flex items-center justify-center">
                  <Activity className="w-6 h-6 text-shadow-pine" />
                </div>
                <div className="ml-4">
                  <p className="text-sm text-shadow-pine/60 font-body">Active This Week</p>
                  <p className="text-2xl font-display font-bold text-obsidian-night">
                    {clients.filter(c => c.streak > 0).length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white">
            <CardContent className="pt-6">
              <div className="flex items-center">
                <div className="w-12 h-12 bg-shadow-pine/20 rounded-full flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-shadow-pine" />
                </div>
                <div className="ml-4">
                  <p className="text-sm text-shadow-pine/60 font-body">Total Workouts</p>
                  <p className="text-2xl font-display font-bold text-obsidian-night">
                    {clients.reduce((sum, c) => sum + (c.workout_count || 0), 0)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Share ID Key Card */}
        <Card className="bg-gradient-to-r from-burnt-terra to-[#A54A2F] text-white mb-8">
          <CardContent className="py-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-display text-xl font-bold mb-1">Share Your Coach ID</h3>
                <p className="text-ivory-mist/80 font-body text-sm">
                  Give this ID key to your clients so they can connect with you during signup
                </p>
              </div>
              <div className="bg-white/20 px-6 py-3 rounded-lg">
                <span className="font-mono text-3xl font-bold">{user?.coach_id_key}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Clients List */}
        <Card className="bg-white">
          <CardHeader>
            <CardTitle className="font-display text-obsidian-night">Your Clients</CardTitle>
            <CardDescription className="font-body text-shadow-pine/60">
              View and monitor client progress
            </CardDescription>
          </CardHeader>
          <CardContent>
            {clients.length === 0 ? (
              <div className="text-center py-12">
                <Users className="w-12 h-12 text-shadow-pine/30 mx-auto mb-4" />
                <p className="text-shadow-pine/60 font-body">No clients yet</p>
                <p className="text-sm text-shadow-pine/40 font-body mt-1">
                  Share your ID key ({user?.coach_id_key}) with clients to get started
                </p>
              </div>
            ) : (
              <div className="divide-y divide-shadow-pine/10">
                {clients.map((client) => (
                  <div
                    key={client.id}
                    className="py-4 flex items-center justify-between hover:bg-ivory-mist/50 -mx-4 px-4 rounded cursor-pointer transition-colors"
                    onClick={() => setSelectedClient(selectedClient?.id === client.id ? null : client)}
                  >
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-shadow-pine/10 rounded-full flex items-center justify-center">
                        <User className="w-5 h-5 text-shadow-pine" />
                      </div>
                      <div className="ml-3">
                        <p className="font-body font-semibold text-obsidian-night">{client.name}</p>
                        <p className="text-sm text-shadow-pine/60 font-body flex items-center">
                          <Mail className="w-3 h-3 mr-1" />
                          {client.email}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-6">
                      <div className="text-center">
                        <p className="text-lg font-bold text-obsidian-night">{client.workout_count || 0}</p>
                        <p className="text-xs text-shadow-pine/60 font-body">Workouts</p>
                      </div>
                      <div className="text-center">
                        <p className="text-lg font-bold text-burnt-terra">{client.streak || 0}</p>
                        <p className="text-xs text-shadow-pine/60 font-body">Streak</p>
                      </div>
                      <ChevronRight className={`w-5 h-5 text-shadow-pine/40 transition-transform ${selectedClient?.id === client.id ? 'rotate-90' : ''}`} />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Expanded Client Details */}
            {selectedClient && (
              <div className="mt-4 p-4 bg-ivory-mist rounded-lg">
                <h4 className="font-display font-bold text-obsidian-night mb-3">
                  {selectedClient.name}'s Assessment
                </h4>
                {selectedClient.assessment_data ? (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <p className="text-xs text-shadow-pine/60 font-body">Handedness</p>
                      <p className="font-semibold text-obsidian-night">{selectedClient.assessment_data.handedness || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-shadow-pine/60 font-body">Twist Direction</p>
                      <p className="font-semibold text-obsidian-night capitalize">{selectedClient.assessment_data.twist_direction || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-shadow-pine/60 font-body">Lead Arm</p>
                      <p className="font-semibold text-obsidian-night">{selectedClient.assessment_data.lead_arm || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-shadow-pine/60 font-body">Weaker Balance Leg</p>
                      <p className="font-semibold text-obsidian-night">{selectedClient.assessment_data.weaker_balance_leg || 'N/A'}</p>
                    </div>
                  </div>
                ) : (
                  <p className="text-shadow-pine/60 font-body text-sm">No assessment data yet</p>
                )}
                <div className="mt-3 pt-3 border-t border-shadow-pine/10">
                  <p className="text-xs text-shadow-pine/60 font-body">
                    Last workout: {formatDate(selectedClient.last_workout)}
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
      <BrandMark />
    </div>
  );
};

export default CoachDashboard;
